/**
 * View: Lista de propostas (admin).
 *
 * Tabela com: Número · Tipo · Cliente · Vendedor · Valor · Status · Criada em.
 * Filtros (client-side): busca livre + Tipo + Vendedor + Status.
 * Rodapé com somatória dos valores filtrados.
 */

import type { Proposta, StatusProposta } from '@infomatic/shared';

import { api } from '../../lib/api.js';
import { getUsuarioLogado } from '../../lib/auth.js';
import { fmtDate } from '../../lib/formatters.js';
import { fmtBRL } from '../../lib/pricing.js';
import { escapeHtml, renderLayout } from '../layout.js';

import { badgeClass, formatStatus } from './status-helpers.js';

interface FiltroState {
  busca: string;
  tipo: string;
  vendedor: string;
  status: string;
  dataInicio: string; // yyyy-mm-dd (input date)
  dataFim: string;
}

const filtro: FiltroState = {
  busca: '',
  tipo: '',
  vendedor: '',
  status: '',
  dataInicio: '',
  dataFim: '',
};
let todasPropostas: Proposta[] = [];

function rotuloTipo(tipo: Proposta['tipo']): string {
  switch (tipo) {
    case 'contrato-recorrente':
      return 'Contrato';
    case 'projeto-infra':
      return 'Projeto · Infra';
    case 'projeto-cftv':
      return 'Projeto · CFTV';
    case 'projeto-controle-acesso':
      return 'Projeto · Controle Acesso';
    case 'venda-pecas':
      return 'Venda · Peças';
    case 'venda-computador':
      return 'Venda · Computador';
    case 'venda-distribuidor':
      return 'Venda · Distribuidor';
    case 'venda-projeto-servico':
      return 'Serviço';
    default:
      return tipo;
  }
}

function badgeClassTipo(tipo: Proposta['tipo']): string {
  if (tipo === 'contrato-recorrente') return 'contrato';
  if (tipo.startsWith('projeto-')) return 'projeto';
  if (tipo === 'venda-projeto-servico') return 'servico';
  return 'venda';
}

function valorMensal(p: Proposta): number {
  if (p.tipo !== 'contrato-recorrente') return 0;
  return p.pricingSnapshot?.breakdown.mensalRecorrente ?? p.pricing?.margem.receita ?? 0;
}

function valorUnico(p: Proposta): number {
  if (p.tipo === 'contrato-recorrente') return 0;
  return p.pricing?.margem.receita ?? 0;
}

function valorPara(p: Proposta): string {
  if (p.tipo === 'contrato-recorrente') {
    const v = valorMensal(p);
    if (v <= 0) return '<span class="lp-vazio">—</span>';
    return fmtBRL(v) + ' <span class="lp-periodo">/mês</span>';
  }
  const v = valorUnico(p);
  if (v <= 0) return '<span class="lp-vazio">—</span>';
  return fmtBRL(v);
}

function aplicarFiltros(items: Proposta[]): Proposta[] {
  // Converte yyyy-mm-dd em timestamps inclusivos (00:00 início / 23:59:59 fim)
  const tsInicio = filtro.dataInicio ? new Date(filtro.dataInicio + 'T00:00:00').getTime() : null;
  const tsFim = filtro.dataFim ? new Date(filtro.dataFim + 'T23:59:59.999').getTime() : null;
  return items.filter((p) => {
    if (filtro.tipo && p.tipo !== filtro.tipo) return false;
    if (filtro.vendedor && p.vendedor.email !== filtro.vendedor) return false;
    if (filtro.status && p.status !== filtro.status) return false;
    if (tsInicio !== null || tsFim !== null) {
      const t = new Date(p.dataCriacao).getTime();
      if (tsInicio !== null && t < tsInicio) return false;
      if (tsFim !== null && t > tsFim) return false;
    }
    if (filtro.busca) {
      const q = filtro.busca.toLowerCase();
      const hay = (
        p.numero +
        ' ' +
        p.cliente.empresa +
        ' ' +
        (p.cliente.responsavel ?? '') +
        ' ' +
        p.vendedor.email
      ).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function vendedoresUnicos(items: Proposta[]): string[] {
  const set = new Set<string>();
  items.forEach((p) => set.add(p.vendedor.email));
  return Array.from(set).sort();
}

export async function renderListaPropostas(): Promise<void> {
  const $app = document.getElementById('app');
  if (!$app) return;

  filtro.busca = '';
  filtro.tipo = '';
  filtro.vendedor = '';
  filtro.status = '';
  filtro.dataInicio = '';
  filtro.dataFim = '';

  $app.innerHTML = renderLayout({
    title: 'Propostas',
    activeNav: 'propostas',
    bodyHtml:
      '<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--sp-6)"><h1 style="font-family: var(--font-display); font-size: var(--fs-3xl); margin: 0">Propostas</h1><a href="/propostas/nova" data-route class="btn btn-primary">+ Nova proposta</a></div><section class="card" id="lista-card"><div>Carregando propostas…</div></section>',
  });

  injectStyles();

  const usuario = getUsuarioLogado();
  const $lista = document.getElementById('lista-card');
  if (!$lista) return;

  if (!usuario) {
    $lista.innerHTML =
      '<p style="color: var(--color-text-secondary)">Faça login para ver suas propostas.</p>';
    return;
  }

  try {
    const result = await api.propostas.list({ pageSize: 200 });
    todasPropostas = result.items;
    if (todasPropostas.length === 0) {
      $lista.innerHTML =
        '<p style="color: var(--color-text-secondary)">Nenhuma proposta cadastrada ainda.</p><a href="/propostas/nova" data-route class="btn btn-primary">Criar primeira proposta</a>';
      return;
    }
    renderConteudo();
    bindFiltros();
  } catch (err) {
    $lista.innerHTML =
      '<div class="badge badge-error">' +
      escapeHtml(err instanceof Error ? err.message : 'Erro ao carregar') +
      '</div>';
  }
}

const STATUS_OPCOES: StatusProposta[] = [
  'rascunho',
  'enviada',
  'visualizada',
  'aceita',
  'aguardando-dados-contrato',
  'contrato-gerado',
  'aguardando-assinatura',
  'assinada',
  'implantacao',
  'concluida',
  'perdida',
  'cancelada',
];

const TIPO_OPCOES: { value: string; label: string }[] = [
  { value: 'contrato-recorrente', label: 'Contrato recorrente' },
  { value: 'projeto-infra', label: 'Projeto · Infra' },
  { value: 'projeto-cftv', label: 'Projeto · CFTV' },
  { value: 'projeto-controle-acesso', label: 'Projeto · Controle Acesso' },
  { value: 'venda-pecas', label: 'Venda · Peças' },
  { value: 'venda-computador', label: 'Venda · Computador' },
  { value: 'venda-distribuidor', label: 'Venda · Distribuidor' },
  { value: 'venda-projeto-servico', label: 'Serviço' },
];

function renderConteudo(): void {
  const $lista = document.getElementById('lista-card');
  if (!$lista) return;

  const visiveis = aplicarFiltros(todasPropostas);
  const totalMensal = visiveis.reduce((acc, p) => acc + valorMensal(p), 0);
  const totalUnico = visiveis.reduce((acc, p) => acc + valorUnico(p), 0);
  const vendedores = vendedoresUnicos(todasPropostas);
  const temFiltro = !!(
    filtro.busca ||
    filtro.tipo ||
    filtro.vendedor ||
    filtro.status ||
    filtro.dataInicio ||
    filtro.dataFim
  );

  let rowsHtml = '';
  if (visiveis.length === 0) {
    rowsHtml =
      '<tr><td colspan="7" class="lp-empty">Nenhuma proposta corresponde aos filtros aplicados.</td></tr>';
  } else {
    rowsHtml = visiveis
      .map((p) => {
        const linkNum =
          '<a href="/propostas/' +
          encodeURIComponent(p.id) +
          '" data-route>' +
          escapeHtml(p.numero) +
          '</a>';
        const badgeTipo =
          '<span class="lp-tipo lp-tipo-' +
          badgeClassTipo(p.tipo) +
          '">' +
          escapeHtml(rotuloTipo(p.tipo)) +
          '</span>';
        const badgeStatus =
          '<span class="badge badge-' +
          badgeClass(p.status) +
          '">' +
          escapeHtml(formatStatus(p.status)) +
          '</span>';
        return (
          '<tr>' +
          '<td class="lp-num">' +
          linkNum +
          '</td>' +
          '<td>' +
          badgeTipo +
          '</td>' +
          '<td>' +
          escapeHtml(p.cliente.empresa) +
          '</td>' +
          '<td class="lp-vendedor">' +
          escapeHtml(p.vendedor.email) +
          '</td>' +
          '<td class="lp-valor">' +
          valorPara(p) +
          '</td>' +
          '<td>' +
          badgeStatus +
          '</td>' +
          '<td class="lp-data">' +
          fmtDate(p.dataCriacao) +
          '</td>' +
          '</tr>'
        );
      })
      .join('');
  }

  const opcoesTipo = TIPO_OPCOES.map(
    (o) =>
      '<option value="' +
      o.value +
      '"' +
      (filtro.tipo === o.value ? ' selected' : '') +
      '>' +
      escapeHtml(o.label) +
      '</option>',
  ).join('');
  const opcoesVendedor = vendedores
    .map(
      (v) =>
        '<option value="' +
        escapeHtml(v) +
        '"' +
        (filtro.vendedor === v ? ' selected' : '') +
        '>' +
        escapeHtml(v) +
        '</option>',
    )
    .join('');
  const opcoesStatus = STATUS_OPCOES.map(
    (s) =>
      '<option value="' +
      s +
      '"' +
      (filtro.status === s ? ' selected' : '') +
      '>' +
      escapeHtml(formatStatus(s)) +
      '</option>',
  ).join('');

  const blocoFiltros =
    '<div class="lp-filtros">' +
    '<div class="lp-filtro lp-filtro-busca">' +
    '<label for="lp-busca">Buscar</label>' +
    '<input id="lp-busca" type="search" placeholder="Número, cliente, vendedor…" value="' +
    escapeHtml(filtro.busca) +
    '" />' +
    '</div>' +
    '<div class="lp-filtro">' +
    '<label for="lp-tipo">Tipo</label>' +
    '<select id="lp-tipo"><option value="">Todos</option>' +
    opcoesTipo +
    '</select>' +
    '</div>' +
    '<div class="lp-filtro">' +
    '<label for="lp-vendedor">Vendedor</label>' +
    '<select id="lp-vendedor"><option value="">Todos</option>' +
    opcoesVendedor +
    '</select>' +
    '</div>' +
    '<div class="lp-filtro">' +
    '<label for="lp-status">Status</label>' +
    '<select id="lp-status"><option value="">Todos</option>' +
    opcoesStatus +
    '</select>' +
    '</div>' +
    '<div class="lp-filtro lp-filtro-data">' +
    '<label for="lp-data-inicio">Período · de</label>' +
    '<input id="lp-data-inicio" type="date" value="' +
    escapeHtml(filtro.dataInicio) +
    '" />' +
    '</div>' +
    '<div class="lp-filtro lp-filtro-data">' +
    '<label for="lp-data-fim">Período · até</label>' +
    '<input id="lp-data-fim" type="date" value="' +
    escapeHtml(filtro.dataFim) +
    '" />' +
    '</div>' +
    (temFiltro
      ? '<button type="button" id="lp-limpar" class="lp-limpar">Limpar filtros</button>'
      : '') +
    '</div>';

  const totalRecorrenteBloco =
    totalMensal > 0
      ? '<div class="lp-total-bloco"><span class="lp-total-label">Total recorrente</span><strong class="lp-total-valor">' +
        fmtBRL(totalMensal) +
        ' <span class="lp-periodo">/mês</span></strong></div>'
      : '';
  const totalUnicoBloco =
    totalUnico > 0
      ? '<div class="lp-total-bloco"><span class="lp-total-label">Total único (projetos/vendas)</span><strong class="lp-total-valor">' +
        fmtBRL(totalUnico) +
        '</strong></div>'
      : '';

  const blocoTotais =
    '<div class="lp-totais">' +
    '<div class="lp-totais-meta"><strong>' +
    visiveis.length +
    '</strong> de ' +
    todasPropostas.length +
    ' proposta(s)</div>' +
    '<div class="lp-totais-valores">' +
    totalRecorrenteBloco +
    totalUnicoBloco +
    '</div>' +
    '</div>';

  const blocoTabela =
    '<div class="lp-tabela-wrap"><table class="lp-tabela">' +
    '<thead><tr>' +
    '<th>Número</th><th>Tipo</th><th>Cliente</th><th>Vendedor</th>' +
    '<th class="lp-valor">Valor</th><th>Status</th><th>Criada em</th>' +
    '</tr></thead>' +
    '<tbody>' +
    rowsHtml +
    '</tbody>' +
    '</table></div>';

  $lista.innerHTML = blocoFiltros + blocoTabela + blocoTotais;
}

function bindFiltros(): void {
  const $busca = document.getElementById('lp-busca') as HTMLInputElement | null;
  const $tipo = document.getElementById('lp-tipo') as HTMLSelectElement | null;
  const $vendedor = document.getElementById('lp-vendedor') as HTMLSelectElement | null;
  const $status = document.getElementById('lp-status') as HTMLSelectElement | null;
  const $dataInicio = document.getElementById('lp-data-inicio') as HTMLInputElement | null;
  const $dataFim = document.getElementById('lp-data-fim') as HTMLInputElement | null;
  const $limpar = document.getElementById('lp-limpar') as HTMLButtonElement | null;

  let timer: ReturnType<typeof setTimeout> | undefined;
  $busca?.addEventListener('input', () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      filtro.busca = $busca.value.trim();
      renderConteudo();
      bindFiltros();
      const $b = document.getElementById('lp-busca') as HTMLInputElement | null;
      if ($b) {
        $b.focus();
        $b.setSelectionRange($b.value.length, $b.value.length);
      }
    }, 200);
  });

  $tipo?.addEventListener('change', () => {
    filtro.tipo = $tipo.value;
    renderConteudo();
    bindFiltros();
  });
  $vendedor?.addEventListener('change', () => {
    filtro.vendedor = $vendedor.value;
    renderConteudo();
    bindFiltros();
  });
  $status?.addEventListener('change', () => {
    filtro.status = $status.value;
    renderConteudo();
    bindFiltros();
  });
  $dataInicio?.addEventListener('change', () => {
    filtro.dataInicio = $dataInicio.value;
    renderConteudo();
    bindFiltros();
  });
  $dataFim?.addEventListener('change', () => {
    filtro.dataFim = $dataFim.value;
    renderConteudo();
    bindFiltros();
  });
  $limpar?.addEventListener('click', () => {
    filtro.busca = '';
    filtro.tipo = '';
    filtro.vendedor = '';
    filtro.status = '';
    filtro.dataInicio = '';
    filtro.dataFim = '';
    renderConteudo();
    bindFiltros();
  });
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const styleEl = document.createElement('style');
  styleEl.textContent = [
    '.lp-filtros { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr 0.9fr 0.9fr auto; gap: var(--sp-3); margin-bottom: var(--sp-4); align-items: end; }',
    '@media (max-width: 1100px) { .lp-filtros { grid-template-columns: 1fr 1fr 1fr; } }',
    '@media (max-width: 700px) { .lp-filtros { grid-template-columns: 1fr 1fr; } }',
    '.lp-filtro { display: flex; flex-direction: column; gap: 4px; }',
    '.lp-filtro label { font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; color: var(--color-text-muted); font-weight: 600; }',
    '.lp-filtro input, .lp-filtro select { padding: 8px 10px; border: 1px solid var(--color-border); border-radius: 6px; font-size: var(--fs-sm); background: #fff; color: var(--color-text); }',
    '.lp-filtro input:focus, .lp-filtro select:focus { outline: 2px solid var(--color-primary, #d4a017); outline-offset: -1px; border-color: var(--color-primary, #d4a017); }',
    '.lp-limpar { padding: 8px 14px; background: #fff; border: 1px solid var(--color-border); border-radius: 6px; cursor: pointer; font-size: var(--fs-sm); color: var(--color-text); font-weight: 500; height: fit-content; }',
    '.lp-limpar:hover { background: #fef2f2; border-color: #dc2626; color: #dc2626; }',
    '.lp-tabela-wrap { overflow-x: auto; }',
    '.lp-tabela { width: 100%; border-collapse: collapse; font-size: var(--fs-sm); }',
    '.lp-tabela thead th { text-align: left; padding: var(--sp-3) var(--sp-4); font-weight: 600; color: var(--color-text); font-size: var(--fs-xs); letter-spacing: 0.5px; text-transform: uppercase; border-bottom: 2px solid var(--color-border); background: var(--color-surface); white-space: nowrap; }',
    '.lp-tabela tbody td { padding: var(--sp-3) var(--sp-4); border-bottom: 1px solid var(--color-border); vertical-align: middle; }',
    '.lp-tabela tbody tr:nth-child(even) { background: var(--color-surface-alt, #fafaf7); }',
    '.lp-tabela tbody tr:nth-child(odd) { background: #fff; }',
    '.lp-tabela tbody tr:hover { background: #fff7e1; }',
    '.lp-tabela tbody tr:last-child td { border-bottom: 0; }',
    '.lp-empty { text-align: center; padding: var(--sp-6) !important; color: var(--color-text-muted); font-style: italic; }',
    '.lp-num a { color: var(--color-text); font-weight: 600; text-decoration: none; font-family: ui-monospace, monospace; }',
    '.lp-num a:hover { color: var(--color-primary, #d4a017); }',
    '.lp-vendedor { color: var(--color-text-secondary); font-size: var(--fs-xs); }',
    '.lp-data { color: var(--color-text-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }',
    '.lp-valor { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; font-weight: 600; }',
    '.lp-periodo { color: var(--color-text-muted); font-weight: 400; font-size: var(--fs-xs); }',
    '.lp-vazio { color: var(--color-text-muted); }',
    '.lp-tipo { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; letter-spacing: 0.3px; white-space: nowrap; }',
    '.lp-tipo-contrato { background: #fff7e1; color: #92651a; border: 1px solid #e6cc8a; }',
    '.lp-tipo-projeto { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }',
    '.lp-tipo-venda { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }',
    '.lp-tipo-servico { background: #f5f3ff; color: #6d28d9; border: 1px solid #ddd6fe; }',
    '.lp-totais { display: flex; justify-content: space-between; align-items: center; margin-top: var(--sp-4); padding: var(--sp-4); background: var(--color-text, #0a0a0a); color: #fff; border-radius: 8px; flex-wrap: wrap; gap: var(--sp-4); }',
    '.lp-totais-meta { color: rgba(255,255,255,0.7); font-size: var(--fs-sm); }',
    '.lp-totais-meta strong { color: #fff; font-size: var(--fs-base); font-weight: 700; }',
    '.lp-totais-valores { display: flex; gap: var(--sp-6); flex-wrap: wrap; }',
    '.lp-total-bloco { display: flex; flex-direction: column; gap: 2px; text-align: right; }',
    '.lp-total-label { font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: rgba(255,255,255,0.6); font-weight: 600; }',
    '.lp-total-valor { font-family: var(--font-display); font-size: var(--fs-xl); font-weight: 700; color: var(--color-primary, #d4a017); }',
    '.lp-total-valor .lp-periodo { color: rgba(255,255,255,0.6); }',
  ].join('\n');
  document.head.appendChild(styleEl);
}
